import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Loader2, Zap } from "lucide-react";
import { api } from "@/lib/api";

// ============================================================================
// TableroCFDILectura — jun-2026. En /admin-saas SOLO queda lectura: contadores
// de producción CFDI y de la Carta Manifiesto. Sin botones de acción: el
// encendido operativo vive en la ficha de cada entidad (/entidades/:id).
// Cada fila enlaza a la ficha de su entidad.
// ============================================================================

const Contador = ({ label, valor, cls, testid }) => (
  <div data-testid={testid} className={`rounded-md px-3 py-2 ${cls}`}>
    <p className="text-lg font-semibold leading-none">{valor}</p>
    <p className="mt-1 text-[11px] font-medium">{label}</p>
  </div>
);

const CHIP = {
  firmada: "bg-emerald-100 text-emerald-800",
  vigente: "bg-emerald-100 text-emerald-800",
  pendiente: "bg-amber-100 text-amber-800",
  por_vencer: "bg-amber-100 text-amber-800",
  vencido: "bg-red-100 text-red-800",
  sin_certificado: "bg-red-100 text-red-800",
  no_verificado: "bg-slate-100 text-slate-700",
  sin_organizacion: "bg-slate-100 text-slate-500",
};

export const TableroCFDILectura = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/sofom/cfdi/entidades-produccion")
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], totales: {}, totales_manifiesto: {} }))
      .finally(() => setLoading(false));
  }, []);

  const t = data?.totales || {};
  const tm = data?.totales_manifiesto || {};
  const items = data?.items || [];

  return (
    <section data-testid="cfdi-tablero-lectura"
             className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
          <Zap className="h-5 w-5 text-amber-700" />
        </span>
        <div>
          <p className="font-heading text-sm font-semibold text-slate-900">
            Producción CFDI — estado de la plataforma
          </p>
          <p className="text-xs text-slate-500">
            Solo lectura. El encendido y la Carta Manifiesto de cada entidad se operan
            desde su ficha (Entidades / Empresas → Ver).
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Contador testid="cfdi-count-produccion-autorizada" label="Producción autorizada"
                  valor={t.produccion_autorizada || 0} cls="bg-emerald-50 text-emerald-800" />
        <Contador testid="cfdi-count-produccion-bloqueada" label="Producción bloqueada"
                  valor={t.produccion_bloqueada || 0} cls="bg-amber-50 text-amber-800" />
        <Contador testid="cfdi-count-sandbox" label="Interno (sandbox)"
                  valor={t.sandbox || 0} cls="bg-slate-100 text-slate-700" />
        <Contador testid="cfdi-count-total" label="Total de entidades"
                  valor={t.total || 0} cls="bg-slate-50 text-slate-600" />
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Contador testid="cfdi-manif-firmada" label="Manifiesto firmado"
                  valor={tm.firmada || 0} cls="bg-emerald-50 text-emerald-800" />
        <Contador testid="cfdi-manif-pendiente" label="Manifiesto pendiente"
                  valor={tm.pendiente || 0} cls="bg-amber-50 text-amber-800" />
        <Contador testid="cfdi-manif-no-verificado" label="Manifiesto no verificado"
                  valor={tm.no_verificado || 0} cls="bg-slate-100 text-slate-700" />
        <Contador testid="cfdi-manif-sin-org" label="Sin organización"
                  valor={tm.sin_organizacion || 0} cls="bg-slate-50 text-slate-600" />
      </div>

      {loading ? (
        <p className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Consultando estado por entidad…
        </p>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500" data-testid="cfdi-tablero-vacio">
          Sin entidades registradas.
        </p>
      ) : (
        <div className="mt-3 overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="p-2 font-semibold">Entidad</th>
                <th className="p-2 font-semibold">RFC</th>
                <th className="p-2 font-semibold">Modo</th>
                <th className="p-2 font-semibold">CSD</th>
                <th className="p-2 font-semibold">Manifiesto</th>
                <th className="p-2 font-semibold">Obs.</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.entity_id}
                    data-testid={`cfdi-tablero-row-${it.entity_id}`}
                    onClick={() => navigate(`/entidades/${it.entity_id}`)}
                    className="cursor-pointer border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-2 font-medium text-slate-800">{it.razon_social}</td>
                  <td className="p-2 font-mono">{it.rfc || "—"}</td>
                  <td className="p-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      it.modo_operacion === "produccion"
                        ? (it.emision_autorizada
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-amber-100 text-amber-800")
                        : "bg-slate-100 text-slate-700"}`}>
                      {it.modo_operacion === "produccion"
                        ? (it.emision_autorizada ? "producción · autorizada" : "producción · bloqueada")
                        : "interno (sandbox)"}
                    </span>
                  </td>
                  <td className="p-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${CHIP[it.csd_estatus] || CHIP.no_verificado}`}>
                      {it.csd_estatus}
                    </span>
                  </td>
                  <td className="p-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${CHIP[it.manifiesto_estatus] || CHIP.no_verificado}`}>
                      {it.manifiesto_estatus}
                    </span>
                  </td>
                  <td className="p-2 text-[11px]">
                    {(it.faltantes || []).length === 0
                      ? <span className="text-emerald-700">✓ ninguna</span>
                      : <span className="text-amber-800">{it.faltantes.length} obs.</span>}
                  </td>
                  <td className="p-2 text-right">
                    <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default TableroCFDILectura;
